import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useAuth } from "@/context/auth";
import { fetchClient } from "@/lib/api-client";

interface UseFetchWeekLeftOrdersParams {
  weekNumber: number;
  orderLimit?: number;
}

export const useFetchWeekLeftOrders = (
  params: UseFetchWeekLeftOrdersParams,
) => {
  const auth = useAuth();
  const { weekNumber, orderLimit = 5 } = params;

  const queryFn = async ({ pageParam = 1 }) => {
    const response = await fetchClient.GET("/nft/week-left-orders", {
      params: {
        query: {
          weekNumber,
          orderPage: typeof pageParam === "number" ? pageParam : 1,
          orderLimit,
        },
      },
    });
    const error = response.error;
    if (error) throw error;
    return response.data;
  };

  const {
    data,
    error,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isFetchNextPageError,
  } = useInfiniteQuery({
    queryKey: ["user", "week-left-orders", weekNumber, orderLimit],
    queryFn,
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const pagination = lastPage?.data?.pagination;

      if (pagination?.hasNextPage) return pagination.page + 1;

      return undefined;
    },
    retry: 1,
    refetchOnReconnect: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchInterval: false,
    refetchIntervalInBackground: false,
  });

  useEffect(() => {
    if (
      error &&
      typeof error === "object" &&
      error !== null &&
      "status" in error &&
      typeof error.status === "number" &&
      error.status === 401
    ) {
      auth.logout();
    }
  }, [error, auth]);

  return {
    data,
    error,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isFetchNextPageError,
  };
};
